import React, { useEffect, useRef, useState } from 'react';
import useInterval from '../../../shared/helpers/custom-hooks/use-interval/useInterval'
import useNavigationDirection from '../../../shared/helpers/custom-hooks/useNavigationDirection/useNavigationDirection'
import { NavigationDirection } from '../../../shared/helpers/custom-hooks/useNavigationDirection/NavigationDirection'
import { IChatsListProps } from './~types/ChatsListProps';
import { connect } from 'react-redux';
import { useNavigate, useParams } from "react-router-dom";
import { 
    getChats, 
    setSearchStringToChatsList, 
    onChatItemClick, 
    updateChats, 
    addNewChatToState,
    onGetChatsModeChanged, 
    changeChatContactChatName,
    searchChats,
    selectChat,
    toggleAutoUpdateChats,
    searchChatsByContactId} from '../../~store/actions/actions';
import { setChatVisibilityToState } from './../../../chats-panel/~store/actions/actions'

import { IApplicationState } from 'src/~store/models/ApplicationState';
import Loader from 'src/app/shared/components/loader/Loader';
import { Table } from 'reactstrap';
import ChatsItem from './chats-item/ChatsItem';
import { 
    channelsListSelector,
    chatsSelector, 
    isReceivingChatsSelector, 
    countOfChatsSelector, 
    searchStringForChatsListSelector,
    operatorIdSelector,
    isModeChangingSelector,
    tagsSelector,
    autoUpdateChatsSelector,
    isAllChatsLoadedSelector,
} from '../../~store/selector';
import { IChatsListRouteParams } from './~types/ChatsListRouteParams';
import './ChatsList.css';
import { IMessage } from '../../~store/models/Message';
import { MessageType } from '../../~store/models/enums/MessageType';
import { IChat } from '../../~store/models/Chat';
import { GetChatsMode } from '../../~store/models/enums/GetChatsMode';
import { REQUESTED_NUMBER_OF_CHATS, SEARCHING_TAGS, TRIGGER_SCROLL_HEIGHT } from './ChatsListConstants';
import { PermissionType } from 'src/app/shared/components/user-context/models/PermissionType';
import { IContact } from 'src/app/contact/~store/models/Contact';
import { isChatVisibleSelector } from 'src/app/contact/~store/selector';
import TagSearchingInput from 'src/app/shared/components/tag-searching-input/TagSearchingInput';
import { ISearchingTag, TagName, TagType } from 'src/app/shared/components/tag-searching-input/~types/models/SearchingTag';
import { allOperatorsSelector } from '../../../operator/~store/selector'
import { getAllOperators } from 'src/app/operator/~store/actions/actions';
import { contactsSelector } from 'src/app/contact/~store/selector';
import { getContacts } from 'src/app/contact/~store/actions/actions';
import { IChatItem } from 'src/app/chats-panel/~store/models/ChatItem';
import { selectedChatSelector } from 'src/app/chats-panel/~store/selectors';
import { useTranslation } from 'react-i18next';
import { getAllChannels } from 'src/app/channel/~store/actions/actions';
import { getTimePassedFromDate } from 'src/app/shared/helpers/date-helper/DateHelper';
import Checkbox from '@mui/material/Checkbox';
import { sLColorSecondsSettingsSelector } from 'src/app/setting/~store/selector';
import { OperatorType } from 'src/app/chats-panel/~store/models/OperatorItem';
import i18n from 'src/app/shared/localization/i18n';

type ReduxType = ReturnType<typeof mapStateToProps> & IChatsListProps;

const ChatsList = (props: ReduxType) => {
    const { t } = useTranslation();
    const params = useParams();
    const navigate = useNavigate();
    const direction = useNavigationDirection();
    const filter: string = params?.filter?.length ? params.filter : '';

    const [rerenderFlag, setRerenderFlag] = useState<boolean>(false);
    const chatsContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if(filter.length > 0) {
            props.onGetChatsModeChanged(GetChatsMode.archive);
        }
        
        const hubConnection = props.hubConnection;
        hubConnection.on('UpdateChats', onUpdateChats); 
        hubConnection.on('NewChat_Broadcast', onNewChatBroadcast);
        props.setSearchString(filter);

        document.addEventListener("keydown", _onKeyDown);
        return () => {
            hubConnection.off('UpdateChats', onUpdateChats);
            hubConnection.off('NewChat_Broadcast', onNewChatBroadcast);
            _onClearFilter();
            document.removeEventListener("keydown", _onKeyDown);
        }
    }, []);

    useEffect(() => {
        if(filter.length > 0) {
            _useFilter(filter);
        }
    }, [direction]);

    useEffect(() => {
        document.removeEventListener("keydown", _onKeyDown);
        document.addEventListener("keydown", _onKeyDown);

        return () => {
            document.removeEventListener("keydown", _onKeyDown);
        }
    }, [props.selectedChatId, props.chats]);

    useInterval(() => {
        if(props.getChatsMode !== GetChatsMode.archive) {
            setRerenderFlag(!rerenderFlag);
        }   
    }, 1000);

    const onUpdateChats = (chats: IChat[]) => {
        props.updateChats(chats);
    }

    const onNewChatBroadcast = (chat: IChat) => {
        props.updateChats([chat]);
    }

    const _onKeyDown = (event: KeyboardEvent) => {
        if(event.key === "PageUp") {
            props.selectChat(false, props.chats, props.selectedChat);
            event.preventDefault();
        }
        if(event.key === "PageDown") {
            props.selectChat(true, props.chats, props.selectedChat);
            event.preventDefault();
        }
    }

    const _useFilter = (filter: string) => {
        if(filter.includes("contact:")) {            
            if (direction === NavigationDirection.Backward) {
                navigate(-1);
            }
            else {
                const contactId = Number.parseInt(filter.split(":")[1]);
                if(!Number.isNaN(contactId) && props.tags.find(tag => tag.tagName === TagName.Contact && tag.value === contactId.toString()) === undefined) {
                    props.searchChatsByContactId(contactId);
                    navigate("/chats");
                }
            }
        }
    }

    const onChatsScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
        if ((e.currentTarget.scrollHeight - e.currentTarget.scrollTop - e.currentTarget.clientHeight) <= TRIGGER_SCROLL_HEIGHT && !props.isReceivingChats && !props.isAllChatsLoaded) {
            props.getChats(props.countOfChats, REQUESTED_NUMBER_OF_CHATS, props.searchString, props.tags, props.getChatsMode);
        }
    }

    const onGetChatsModeChanged = (mode: GetChatsMode) => {
        if(props.isReceivingChats || props.getChatsMode === mode || props.isModeChanging) return;
        props.onGetChatsModeChanged(mode);
        props.getChats(0, REQUESTED_NUMBER_OF_CHATS, props.searchString, props.tags, mode);
    }

    const _searchChats = (tags: ISearchingTag[]) => {
        chatsContainerRef.current?.scrollTo({top: 0});
        props.searchChats(tags);
    }

    const _onClearFilter = () => {
        _searchChats([]);
    }

    const onChatItemClickHandler = (chatId: number) => {
        props.onChatItemClick(chatId);

        setChatVisibilityToAll(true);
    }

    const setChatVisibilityToAll = (value: boolean) => {
        props.setChatVisibility(value);
        props.setChatVisibilityToState(value);
    };

    const GetChatsTable = (props: ReduxType): React.ReactElement<{}> => {
        const { t } = useTranslation();
        return (
            <React.Fragment>
                {(props.chats.length > 0) &&
                    <Table className="chats-list-table card-table">
                        <thead>
                            <tr>
                                <th className="chat-item__chat-id-cell"></th>
                                <th className="chat-item__channel-info-cell">
                                    {t('common.contact')}
                                </th>
                                <th style={props.isChatVisible ? {} : { float: "right" }}>{t('chats.info')}</th>
                                {!props.isChatVisible && (
                                    <th>{t('common.channel')}</th>
                                )}
                                {props.getChatsMode === GetChatsMode.archive && <th>{t('common.status')}</th>}
                                {!props.isChatVisible && <th>{t('chats.statistics')}</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {props.chats.map((chat, index) => {
                                return (
                                    <ChatsItem
                                        chat={chat}
                                        key={`${chat.chatId}-${index}`}
                                        sender={getSender(chat.lastMessage, props.operatorId)}
                                        onClick={(chatId) => { if (chatId !== props.selectedChatId || !props.isChatVisible) onChatItemClickHandler(chatId) }}
                                        selected={chat.chatId === props.selectedChatId}
                                        isChatvisible={props.isChatVisible}
                                        operatorId={props.operatorId}
                                        changeChatContactChatname={props.changeChatContactChatname}
                                        chatsMode={props.getChatsMode}
                                        firstNotAnsweredMessage={chat.firstNotAnsweredMessage}
                                        sLColorSecondsSettings={props.sLColorSecondsSettings}
                                        pausedTime={chat.lastPausedTime ? getTimePassedFromDate(chat.lastPausedTime) : ''}
                                        contact={chat.contact}
                                    />
                                )
                            })}
                        </tbody>
                    </Table>
                }
            </React.Fragment>
        )
    }

    return (
        <div className="chats-list-container">
            <div className="chats-list__header">
                <div className="chats-list__search-mode">
                    <span
                        className={props.getChatsMode === GetChatsMode.my ? "active" : ""}
                        tabIndex={0}
                        onClick={() => { onGetChatsModeChanged(GetChatsMode.my) }}
                        onKeyDown={(event: any) => {
                            if (event.key === "Enter") {
                                onGetChatsModeChanged(GetChatsMode.my)
                            }
                        }}
                    >
                        {t('chats.my')}
                    </span>
                    <span
                        className={props.getChatsMode === GetChatsMode.withMe ? "active" : ""}
                        tabIndex={0}
                        onClick={() => { onGetChatsModeChanged(GetChatsMode.withMe) }}
                        onKeyDown={(event: any) => {
                            if (event.key === "Enter") {
                                onGetChatsModeChanged(GetChatsMode.withMe)
                            }
                        }}
                    >
                        {t('chats.withMe')}
                    </span>
                    <span
                        className={props.getChatsMode === GetChatsMode.all || props.getChatsMode === GetChatsMode.botsAndQueue ? "active" : ""}
                        tabIndex={0}
                        onClick={() => { onGetChatsModeChanged(props.permissionList.includes(PermissionType.Chats_ManageOthersChats) ? GetChatsMode.all : GetChatsMode.botsAndQueue) }}
                        onKeyDown={(event: any) => {
                            if (event.key === "Enter") {
                                onGetChatsModeChanged(props.permissionList.includes(PermissionType.Chats_ManageOthersChats) ? GetChatsMode.all : GetChatsMode.botsAndQueue)
                            }
                        }}
                    >
                        {t('chats.all')}
                    </span>
                    <span
                        className={props.getChatsMode === GetChatsMode.archive ? "active" : ""}
                        tabIndex={0}
                        onClick={() => { onGetChatsModeChanged(GetChatsMode.archive) }}
                        onKeyDown={(event: any) => {
                            if (event.key === "Enter") {
                                onGetChatsModeChanged(GetChatsMode.archive)
                            }
                        }}
                    >
                        {t('chats.archive')}
                    </span>
                </div>
                <TagSearchingInput
                    searchingTags={SEARCHING_TAGS}
                    operators={props.operators}
                    contacts={props.contacts}
                    channels={props.channels}
                    getAllChannels={props.getAllChannels}
                    getAllOperators={props.getAllOperators}
                    getContacts={(searchingString) => props.getContacts(0, 5, searchingString ?? "", true, true)}
                    onSearch={(tags) => _searchChats(tags)}
                    currentTags={props.tags}
                    onClearFilter={() => _onClearFilter()} />
            </div>
            <div className='auto-update-chats-container'>
                <Checkbox 
                    className='auto-update-chats-check' 
                    color="success"
                    checked={props.autoUpdateChats} 
                    onClick={props.toggleAutoUpdateChats}
                    onKeyUp={(e) => e.key === "Enter" && props.toggleAutoUpdateChats()}
                />
                <span>{t('chats.autoUpdateChat')}</span>
            </div>
            <div 
                ref={chatsContainerRef}
                onScroll={onChatsScroll} 
                className={`chats-list__body ${(props.isChatVisible ? "splittered" : "")}`}>
                {GetChatsTable(props)}
                {props.isReceivingChats && <Loader/>}
            </div>
        </div>
    )
}





const getSender = (message: IMessage, myId : number): string | null => {
    if(message) {
        if(message.messageType === MessageType.ClientMessage)
        {
            return i18n.t('chats.messageFromClient');
        }
        if(message.operatorId === myId) {
            return i18n.t('chats.messageFromMyself');
        }
        if (message.operatorModel?.type === OperatorType.Bot) {
            return i18n.t('chats.messageFromBot');
        }
        return i18n.t('chats.messageFromOperator');
    }
    return null;
}

const mapStateToProps = (state: IApplicationState, externalProps: any) => {
    return {
        channels: channelsListSelector(state),
        chats: chatsSelector(state),
        isAllChatsLoaded: isAllChatsLoadedSelector(state),
        isReceivingChats: isReceivingChatsSelector(state),
        countOfChats: countOfChatsSelector(state),
        searchString: searchStringForChatsListSelector(state),
        operatorId: operatorIdSelector(state),
        sLColorSecondsSettings: sLColorSecondsSettingsSelector(state),
        isChatVisible: isChatVisibleSelector(state),
        selectedChatId: state.chatItemsState.selectedChatId,
        hubConnection: state.chatItemsState.hubConnection,
        getChatsMode: state.chatsState.chatsList.getChatsMode,
        isModeChanging: isModeChangingSelector(state),
        operators: allOperatorsSelector(state),
        contacts: contactsSelector(state),
        tags: tagsSelector(state),
        selectedChat: selectedChatSelector(state),
        setChatVisibility: externalProps.setChatVisibility,
        permissionList: state.userContextState.permissions.permissionList,
        autoUpdateChats: autoUpdateChatsSelector(state),
    };
}

const mapDispatchToProps = (dispatch: any): IChatsListProps => {
    return {
        getAllChannels: () => dispatch(getAllChannels()),
        getChats: (
            skip: number, 
            count: number, 
            searchString: string,
            tags: ISearchingTag[],
            getChatsMode: GetChatsMode
        ) => dispatch(getChats(skip, count, searchString, tags, getChatsMode)),
        setSearchString: (
            searchString: string,
        ) => dispatch(setSearchStringToChatsList(searchString)),
        onChatItemClick: (chatId: number) => dispatch( onChatItemClick(chatId)),
        setChatVisibilityToState: (value: boolean) => dispatch(setChatVisibilityToState(value)),
        updateChats: (chats: IChat[]) => dispatch(updateChats(chats)),
        onGetChatsModeChanged: (mode: GetChatsMode) => dispatch(onGetChatsModeChanged(mode)),
        changeChatContactChatname: (
            contact: IContact, 
            chatId: number
        ) => dispatch(changeChatContactChatName(contact, chatId)),
        addNewChatToState: (chat: IChat) => dispatch(addNewChatToState(chat)),
        getAllOperators: () => dispatch(getAllOperators()),
        getContacts: (
            skip: number, 
            count: number, 
            searchString: string | undefined, 
            isSortingByContactLastName: boolean
        ) => dispatch(getContacts(skip, count, searchString, isSortingByContactLastName, true)),
        searchChats: (
            tags: ISearchingTag[]
        ) => dispatch(searchChats(tags)),
        selectChat: (
            isNext: boolean,
            chats: IChat[],
            selectedChat: IChatItem | undefined,
        ) => dispatch(selectChat(isNext, chats, selectedChat)),
        toggleAutoUpdateChats: () => dispatch(toggleAutoUpdateChats()),
        searchChatsByContactId: (contactId: number) => dispatch(searchChatsByContactId(contactId))
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ChatsList);